/**
 * Shared HLS logic: detect HLS URLs, native vs hls.js, attach/destroy Hls instance.
 * Used by VideoContainer; reusable by other components (e.g. app AssetPreviewDialog) that need
 * the same Safari-native vs hls.js branching.
 */
import type { ErrorData } from 'hls.js'
import Hls from 'hls.js'

const HLS_INSTANCE_KEY = '__useHlsInstance'

export function isHlsUrl(url: string): boolean {
  return typeof url === 'string' && (url.includes('.m3u8') || url.endsWith('.m3u8'))
}

export const isNativeHlsSupported
  = typeof document !== 'undefined'
    && document.createElement('video').canPlayType('application/vnd.apple.mpegurl') !== ''

export function getUseHlsJs(isHls: boolean, _src: string): boolean {
  return isHls && !isNativeHlsSupported
}

export function destroyHls(el: HTMLVideoElement): void {
  const hls = (el as unknown as Record<string, Hls | undefined>)[HLS_INSTANCE_KEY]
  if (hls) {
    hls.destroy()
    delete (el as unknown as Record<string, Hls | undefined>)[HLS_INSTANCE_KEY]
  }
}

/** Subset of HlsConfig relevant for capacity-tier tuning (avoids importing full HlsConfig type). */
export interface HlsCapacityConfig { maxBufferLength?: number, maxMaxBufferLength?: number }

/** Optional hooks so the player can show a visible error instead of a black video surface. */
export interface AttachHlsCallbacks {
  onUnsupportedInBrowser?: () => void
  /** Invoked with hls.js error payload on fatal `ERROR` (for Sentry / diagnostics). */
  onFatalStreamError?: (data: ErrorData) => void
}

export function attachHls(
  el: HTMLVideoElement,
  src: string,
  useHlsJs: boolean,
  config?: HlsCapacityConfig,
  callbacks?: AttachHlsCallbacks
): void {
  if (!useHlsJs || !src) {
    destroyHls(el)
    return
  }
  if (!Hls.isSupported()) {
    destroyHls(el)
    callbacks?.onUnsupportedInBrowser?.()
    return
  }
  destroyHls(el)
  const hls = new Hls(config ?? {})
  ;(el as unknown as Record<string, Hls>)[HLS_INSTANCE_KEY] = hls
  hls.on(Hls.Events.ERROR, (_event, data) => {
    if (data.fatal) {
      callbacks?.onFatalStreamError?.(data)
    }
  })
  hls.loadSource(src)
  hls.attachMedia(el)
}
